import {boundMethod} from "autobind-decorator";
import React from "react";
import {injectIntl, IntlShape} from "react-intl";
import {Link} from "react-router-dom";

import {EExpireReasons, ERoles} from "@/services/models";
import session from "@/services/session";
import license, {ILicenseStatus} from "./services/license";
import resize from "./services/resize";

import FooterExtra from "@/components/info-global/FooterExtra";
import {sepviewFavicon} from "@/components/SessionExpiredNotification";
import {getLongDate} from "@toolbox/design/Time";
import {intl2Str, parseNumber} from "@translate/T";
import {parseISO} from "date-fns";
import {ERROR_RED, TEXT_MUTED, WARNING_ORANGE} from "./styles/_variables";

export function getSepviewVersion() {
    return "SEPView" + trademark() + " " + APP_VERSION;
}

export function trademark() {
    return "\u00ae";
}

interface IFooterProps {
    intl: IntlShape;
}

interface IFooterState {
    fixed: string;
    licenseStatus: ILicenseStatus;
}

class Footer extends React.PureComponent<IFooterProps, IFooterState> {
    public readonly state: IFooterState = {
        fixed: "",
        licenseStatus: license.status,
    };

    private readonly footer = React.createRef<HTMLDivElement>();
    private unsubscribe?: () => void;

    public componentDidMount() {
        const unLicense = license.subscribe({
            licenseChanged: (licenseStatus) => this.setState({licenseStatus}),
        });
        const unSession = session.subscribe({
            expired: (reason) => {
                if (reason === EExpireReasons.LoggedOut) {
                    this.forceUpdate();
                }
            },
        });
        const unResize = resize.subscribe(document.body, this.calcFixed);

        this.unsubscribe = () => {
            unLicense();
            unSession();
            unResize();
        };
        if (!this.state.licenseStatus.expiry) {
            void license.retrieveLicense();
        }
        this.calcFixed();
    }

    public componentWillUnmount() {
        this.unsubscribe?.();
    }

    public render() {
        const {fixed} = this.state;

        return (
            <footer
                ref={this.footer}
                className={`container-fluid${fixed} pb-2`} // padding, hence it will be counted towards height
            >
                <hr className="mt-0" />
                {this.renderFooter()}
            </footer>
        );
    }

    // return a color, depending on the license status for unlimited and escalated for left days.
    // with unlimited let it light gray, otherwise escalate color based on days left
    private getLicenseColor(licenseStatus: ILicenseStatus) {
        if (licenseStatus.daysLeft <= 1) {
            return ERROR_RED; // red for critical
        }

        if (licenseStatus.daysLeft <= 14) {
            return WARNING_ORANGE; // orange for warning
        }

        return TEXT_MUTED; // gray in general
    }

    private renderLicenseStatus(intl: IntlShape) {
        const {licenseStatus} = this.state;

        if (!licenseStatus.expiry) {
            return null;
        }

        if (licenseStatus.isUnlimited) {
            return null;
        }

        const expiryDateISO = parseISO(licenseStatus.expiry);

        const licenseText =
            licenseStatus.daysLeft <= 0
                ? intl2Str(intl, "Your license is expired.")
                : intl2Str(
                      intl,
                      "License valid for {daysLeft} days until {expiryDate, date}",
                      {
                          daysLeft: Math.max(0, licenseStatus.daysLeft),
                          expiryDate: expiryDateISO,
                      },
                  );

        const isAdministrator =
            session.hasSession && session.hasRole(ERoles.Administrator);
        const licenseTitle = isAdministrator
            ? intl2Str(intl, "Manage license")
            : intl2Str(intl, "License information");
        const licenseContent = isAdministrator ? (
            <Link
                to="/license"
                style={{color: this.getLicenseColor(licenseStatus)}}
            >
                {licenseText}
            </Link>
        ) : (
            licenseText
        );

        return (
            <span
                style={{
                    marginLeft: "auto",
                    color: this.getLicenseColor(licenseStatus),
                }}
                title={licenseTitle}
            >
                {licenseContent}
            </span>
        );
    }

    private renderFooter() {
        const {intl} = this.props;

        return (
            <FooterExtra>
                <span className="form-inline">
                    {sepviewFavicon(intl, true)}
                    <span
                        title={
                            getLongDate(intl, new Date(BUILD_TIME)) +
                            " / " +
                            BUILD_HASH
                        }
                    >
                        {getSepviewVersion()}
                    </span>
                </span>

                <span className="form-inline">
                    <span>
                        {intl2Str(intl, "Copyright {build}", {
                            build: BUILD_YEAR,
                        })}
                        <React.Fragment> &copy; </React.Fragment>
                        <a
                            href="http://www.lum-gmbh.com/"
                            rel="noreferrer"
                            target="_blank"
                        >
                            LUM GmbH
                        </a>
                    </span>
                    {this.renderLicenseStatus(intl)}
                </span>
            </FooterExtra>
        );
    }

    @boundMethod
    private calcFixed() {
        let _body = document.body.clientHeight;
        let _margin = 0; // margin of body of container-col
        const _window = window.innerHeight;
        const _footer = this.footer.current?.clientHeight ?? 0;

        const container = document.getElementById("container-col-connected"); // id which has to match ContainerCol.tsx
        if (container) {
            const bottom = window.getComputedStyle(container).marginBottom;
            _margin = parseNumber(bottom.replace("px", ""));
            _margin -= 16; // substract 16px, cause of <hr/> margin, which is overlapping
        }

        // if no classname, meaning just plain footer, substract it from body
        if (!this.state.fixed) {
            _body -= _footer;
        }

        const fixed =
            _window > _body + _margin + _footer ? " fixed-bottom" : "";
        this.setState({fixed});
    }
}

export default injectIntl(Footer);
